/**
 * ParametersSection Component
 * Displays and manages parameter inputs for data adjustments
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import DateTimeInput from '../../../shared/components/forms/inputs/DateTimeInput';
import NumericInput from '../../../shared/components/forms/inputs/NumericInput';
import SubmitButton from '../../../shared/components/buttons/SubmitButton';
import RestartButton from '../../../shared/components/buttons/RestartButton';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';
import type { DateTimeState, ParameterValidationErrors } from '../types/dataAdjustments.types';

interface ParametersSectionProps {
  startTime: DateTimeState;
  endTime: DateTimeState;
  timeStepSize: string;
  offset: string;
  isConst: boolean;
  anchorTime: string;
  onStartTimeChange: (value: string, timestamp: number | null) => void;
  onEndTimeChange: (value: string, timestamp: number | null) => void;
  onTimeStepSizeChange: (value: string) => void;
  onOffsetChange: (value: string) => void;
  onAnchorTimeChange: (value: string) => void;
  onProcess: () => void;
  onRestart: () => void;
  isLoading: boolean;
  validationErrors?: ParameterValidationErrors;
  showSection: boolean;
  hideButtons?: boolean;
}

const ParametersSection: React.FC<ParametersSectionProps> = ({
  startTime,
  endTime,
  timeStepSize,
  offset,
  isConst,
  anchorTime,
  onStartTimeChange,
  onEndTimeChange,
  onTimeStepSizeChange,
  onOffsetChange,
  onAnchorTimeChange,
  onProcess,
  onRestart,
  isLoading,
  validationErrors,
  showSection,
  hideButtons = false
}) => {
  const { t } = useTranslation('dataAdjustments');

  if (!showSection) return null;

  // Show the offset / anchor-time field only after a number is entered in the
  // time-step field — until then the const/var distinction (and therefore which
  // field to show) is undefined, so we keep it hidden.
  const hasTimeStep =
    timeStepSize.trim() !== '' &&
    Number.isFinite(parseFloat(timeStepSize)) &&
    parseFloat(timeStepSize) > 0;
  
  return (
    <div className="mb-4 sm:mb-8">
      <h2 className="text-base sm:text-lg md:text-xl font-bold mb-3 sm:mb-4 flex items-center">
        {t('parameters.title')}
        <InfoTooltip text={t('tooltips.section')} width="w-96" />
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-4">
        <DateTimeInput
          label={t('startTime.label')}
          value={startTime.value}
          onChange={onStartTimeChange}
          placeholder={t('startTime.placeholder')}
        />
        {validationErrors?.startTime && (
          <p className="text-red-600 text-xs sm:text-sm mt-1">{validationErrors.startTime}</p>
        )}
        <DateTimeInput
          label={t('endTime.label')}
          value={endTime.value}
          onChange={onEndTimeChange}
          placeholder={t('endTime.placeholder')}
        />
        {validationErrors?.endTime && (
          <p className="text-red-600 text-xs sm:text-sm mt-1">{validationErrors.endTime}</p>
        )}
        <div>
          <div className="flex items-center mb-1 sm:mb-2">
            <label className="block text-foreground font-medium text-xs sm:text-sm md:text-base">
              {t('timeStepSize.label')}
            </label>
            <InfoTooltip text={t('tooltips.zeitschrittweite')} />
          </div>
          <NumericInput
            label=""
            value={timeStepSize}
            onChange={onTimeStepSizeChange}
            allowZero={true}
            error={validationErrors?.timeStepSize}
          />
        </div>
        {!hasTimeStep ? null : isConst ? (
          <div>
            <div className="flex items-center mb-1 sm:mb-2">
              <label className="block text-foreground font-medium text-xs sm:text-sm md:text-base">
                {t('offset.label')}
              </label>
              <InfoTooltip text={t('tooltips.offset')} />
            </div>
            <NumericInput
              label=""
              value={offset}
              onChange={onOffsetChange}
              allowZero={true}
              error={validationErrors?.offset}
            />
          </div>
        ) : (
          <div>
            <div className="flex items-center mb-1">
              <label className="block text-sm font-medium text-foreground">
                {t('anchorTime.label')}
              </label>
              <InfoTooltip text={t('tooltips.anchorTime')} />
            </div>
            <input
              type="text"
              value={anchorTime}
              onChange={(e) => onAnchorTimeChange(e.target.value)}
              placeholder="2025-08-15 06:30:00"
              className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {anchorTime && !/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(anchorTime) && (
              <p className="mt-1 text-xs text-red-600">
                {t('anchorTime.formatError')}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Processing and restart buttons */}
      {!hideButtons && (
        <div className="mt-4 sm:mt-8 flex justify-start items-center gap-2 sm:gap-4 flex-wrap">
          <SubmitButton
            onClick={onProcess}
            disabled={isLoading || !timeStepSize || parseFloat(timeStepSize) < 1 || parseFloat(timeStepSize) > 1440 || offset === ''}
          >
            {isLoading ? t('processing', { ns: 'dataProcessing' }) : t('buttons.processData')}
          </SubmitButton>

          <RestartButton
            onRestart={onRestart}
            iconSize={28}
            disabled={isLoading}
            aria-label="Reset all data"
          />
        </div>
      )}
    </div>
  );
};

export default ParametersSection;
